import { HD_URL, PAGE_TYPE } from '../constants';

/**
 * Gets the current promotional image source URL for a given page, based on active EMT content promotions.
 * Returns the image source if a promotion is active for today, not exempt for the page, and has an image.
 *
 * @param {Object} emtContentPromotions - The EMT content promotions object.
 * @param {string} pageName - The name of the page to check for promotion exemptions.
 * @returns {string|undefined} The source URL of the current promotional image, or undefined if none is active.
 */
export const getCurrentPromoImageSrc = (emtContentPromotions, pageName) => {
  if (!emtContentPromotions || typeof emtContentPromotions !== 'object') {
    return undefined;
  }

  const today = new Date();
  const currentPromo = Object.values(emtContentPromotions).find((promo) => {
    if (!promo) {
      return false;
    }

    const { starts, expires, pageExemptions, image } = promo;
    if (!starts || !expires) {
      return false;
    }

    const startDate = new Date(starts);
    const endDate = new Date(expires);

    const isActive = startDate <= today && today < endDate;
    const isPageExempt = Array.isArray(pageExemptions) && pageExemptions.includes(pageName);

    return isActive && !isPageExempt && image?.source;
  });

  return currentPromo?.image?.source;
};

/**
 * Generates options for fetching GraphQL reviews based on the page type and pagination.
 *
 * @param {Object} params - Parameters for generating options.
 * @param {string} params.pageType - The type of page (e.g., PAGE_TYPE.REVIEWS).
 * @param {number} [params.page=1] - The current page number (default is 1).
 * @param {number} [params.pageSize=10] - The number of items per page (default is 10).
 * @param {string} params.storeId - The ID of the store for which to fetch reviews.
 * @returns {Object} - The options object for GraphQL queries.
 */
export const getGraphQlReviewsOptions = ({ pageType, page = 1, pageSize = 10, storeId }) => {
  let startIndex = 1;

  if (pageType === PAGE_TYPE.REVIEWS) {
    startIndex = Math.max((page - 1) * pageSize, 1);
    if (page > 1) {
      startIndex += 1;
    }
  }

  return {
    variables: {
      filters: {
        ageRange: null,
        gender: null,
        isVerifiedPurchase: false,
        profile: null,
        prosCons: null,
        starRatings: null
      },
      pagesize: pageSize.toString(),
      recfirstpage: pageSize.toString(),
      startIndex,
      sortBy: 'photoreview',
      storeId
    }
  };
};

/**
 * Generates a location-based string for a Home Depot department/store.
 *
 * @param {Object} params
 * @param {string} [params.department] - The department name.
 * @param {string} [params.storeName] - The store name.
 * @param {string} [params.city] - The city.
 * @param {string} [params.state] - The state.
 * @param {string} [params.postalCode] - The postalCode code.
 * @returns {string} The generated string.
 */
export const generateStorePageTitle = ({
  department, storeName, city, state, postalCode
}) => {
  let storePageTitle = '';

  if (department) {
    storePageTitle += `${department} at The `;
  } else {
    storePageTitle += 'The ';
  }

  if (storeName) {
    storePageTitle += `${storeName} Home Depot`;
  } else {
    storePageTitle += 'Home Depot';
  }

  if (city && state && postalCode) {
    storePageTitle += ` in ${city}, ${state} ${postalCode}`;
  }

  return storePageTitle;
};

/**
 * Generates the canonical URL for a store page, optionally appending a suffix.
 *
 * @param {Object} params - Parameters for generating the canonical URL.
 * @param {string} params.storeDetailsPageLink - The base path for the store details page.
 * @param {string|null|undefined} params.urlSuffix - The suffix to append to the URL.
 *   If null or undefined, returns null. Allows empty string.
 * @returns {string|null} The canonical URL, or null if required parameters are missing.
 *
 */
export const generateStorePageCanonical = ({ storeDetailsPageLink, urlSuffix }) => {
  if (!storeDetailsPageLink || urlSuffix == null) {
    return null;
  }

  return `${HD_URL}${storeDetailsPageLink}${urlSuffix}`;
};